// @ts-ignore
// @ts-nocheck

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { EventProps } from "react-big-calendar";
import moment from "moment";
import { format } from "date-fns";
import { CheckIcon } from "lucide-react";
import Cookies from "js-cookie";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/alert-dialog";
import { useEditDateSheetModal } from "./use-create-salary-modal";
import { Button } from "@/components/ui/button/button";

import {
  fetchSingleTimesheetByIdForAdminQueryOptions,
  getAllEmployeesQueryOptions,
} from "@/config/queries/query-options";
import { getAttendanceOfEmpByMonthYearForAdmin } from "@/config/queries/admin/admin-queries";
import { AttendanceEventItem } from "./comps/att-types";
import { AttendanceEvent } from "./comps/attendance-event";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Label } from "@/components/ui/label/label";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import Calendar from "./comps/Calendar";
import { Separator } from "@/components/ui/separator/separator";
import { editDatesheetByAdmin } from "@/config/mutation/admin";

type Month = {
  label: string;
  value: number;
  disabled: boolean;
};

const AllMonths = [
  { label: "January", value: 0, disabled: false },
  { label: "February", value: 1, disabled: false },
  { label: "March", value: 2, disabled: false },
  { label: "April", value: 3, disabled: false },
  { label: "May", value: 4, disabled: false },
  { label: "June", value: 5, disabled: false },
  { label: "July", value: 6, disabled: false },
  { label: "August", value: 7, disabled: false },
  { label: "September", value: 8, disabled: false },
  { label: "October", value: 9, disabled: false },
  { label: "November", value: 10, disabled: false },
  { label: "December", value: 11, disabled: false },
] satisfies Month[];

function updateMonthsAvailability(
  months: Month[],
  selectedYear: number
): Month[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  return months.map((month) => {
    if (selectedYear === currentYear) {
      return {
        ...month,
        disabled: month.value >= currentMonth,
      };
    } else if (selectedYear < currentYear) {
      return {
        ...month,
        disabled: false,
      };
    } else {
      return {
        ...month,
        disabled: true,
      };
    }
  });
}

function generateYears(): number[] {
  const currentYear = new Date().getFullYear();
  const endYear = 2024;
  const years: number[] = [];

  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }

  return years;
}

interface MyObject {
  _id: string;
  createdAt: string | Date; // Assuming ISO 8601 string format
  employee: string;
  mode: string;
}

function findObjectByDate(
  objects: MyObject[],
  mode: "Earliest" | "Latest"
): MyObject | null {
  if (objects.length === 0) return null;

  let resultObject: MyObject = objects[0];
  let resultDate: Date = new Date(resultObject.createdAt);

  for (let i = 1; i < objects.length; i++) {
    const currentDate: Date = new Date(objects[i].createdAt);
    if (mode === "Earliest" && currentDate < resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    } else if (mode === "Latest" && currentDate > resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    }
  }

  return resultObject;
}

function getMiddleDayOfMonth(year: number, month: number) {
  // Create a moment object for the first day of the given month and year
  const firstDayOfMonth = moment({ year, month: month, day: 1 });

  // Get the number of days in the month
  const daysInMonth = firstDayOfMonth.daysInMonth();

  // Calculate the middle day
  const middleDay = Math.ceil(daysInMonth / 2);

  // Create a moment object for the middle day of the month
  const middleDate = moment({ year, month: month, day: middleDay });

  // Format the date as desired (e.g., 'YYYY-MM-DD')
  return middleDate.toDate();
}

export const EditTimesheetModal = () => {
  const key = Cookies.get("key") as string;
  const { onOpenChange, open, sheetId } = useEditDateSheetModal();

  const { data: SingleTimesheetData, isPending: isDatesheetPending } = useQuery(
    fetchSingleTimesheetByIdForAdminQueryOptions(key, sheetId as string)
  );

  const queryClient = useQueryClient();

  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() - 1
  );

  const [selectedEmployee, setSelectedEmployee] = useState("");

  const [date, setDate] = useState(() =>
    getMiddleDayOfMonth(selectedYear, selectedMonth)
  );

  const months = useMemo(() => {
    const result = updateMonthsAvailability(AllMonths, selectedYear);

    return result;
  }, [selectedMonth, selectedYear]);

  const years = useMemo(() => generateYears(), []);

  const { data, mutate, isPending } = useMutation({
    mutationKey: ["attendance-with-monthyear"],
    mutationFn: getAttendanceOfEmpByMonthYearForAdmin(key),
  });

  // const { data: SingleEmpData, isPending: isSingleEmpLoading } = useQuery(
  //   getSingleEmployeeQueryOptions(key, selectedEmployee)
  // );

  const {
    data: EmployeesData,
    isPending: isEmployeesLoading,
    isFetching,
  } = useQuery(getAllEmployeesQueryOptions(key));

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (EmployeesData === undefined || EmployeesData?.employees === undefined) {
      return [];
    }

    const result = EmployeesData?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [EmployeesData, isEmployeesLoading, isFetching]);

  const ATTENDANCE_EVENTS = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.attendance === undefined) {
      return [];
    }

    const result = data?.attendance?.map((att) => {
      const from = moment(att?.date)?.toDate();
      const to = moment(att?.date)?.toDate();

      // console.log("Ätt", att);

      const checkin = findObjectByDate(att?.checkin, "Earliest");
      const checkout = findObjectByDate(att?.checkout, "Latest");

      // console.log({
      //   checkin,
      //   checkout,
      // });

      return {
        start: from,
        end: to,
        data: {
          attendance: {
            id: att?._id,
            createdAt: att?.createdAt,
            status: att?.status,
            date: att?.date,
            hoursWorked: att?.hoursWorked,
            ...(checkin !== null && checkin?.createdAt
              ? {
                  checkinTime: format(
                    new Date(checkin?.createdAt as string),
                    "p"
                  ),
                }
              : {
                  checkinTime: "",
                }),
            ...(checkout !== null && checkout?.createdAt
              ? {
                  checkoutTime: format(
                    new Date(checkout?.createdAt as string),
                    "p"
                  ),
                }
              : {
                  checkoutTime: "",
                }),
            // checkinTime: format(new Date(checkin?.createdAt as string), "PPpp"),
            // checkoutTime: format(
            //   new Date(checkout?.createdAt as string),
            //   "PPpp"
            // ),
          },
        },
      };
    });

    // const holidays = SingleEmpData?.employee?.holidayList?.holidays as {
    //   holidayName: string;
    //   date: string;
    //   description: string;
    // }[];

    // // @ts-ignore
    // const holidaysMarking = holidays?.map((holiday) => {
    //   const from = moment(holiday?.date)?.toDate();
    //   const to = moment(holiday?.date)?.toDate();

    //   return {
    //     start: from,
    //     end: to,
    //     data: {
    //       holiday: {
    //         status: holiday?.holidayName,
    //         date: holiday?.date,
    //       },
    //     },
    //   };
    // });

    return [...result];
  }, [data, isPending]);

  const detailingFilterEvents = useMemo(() => {
    const seenStartDates = new Set<string>();

    return ATTENDANCE_EVENTS.filter((item) => {
      const startDate = new Date(item.start);
      const yearMonthStr = `${startDate.getFullYear()}-${String(
        startDate.getMonth() + 1
      ).padStart(2, "0")}-${startDate.getDate()}`;

      if (
        startDate.getFullYear() === selectedYear &&
        startDate.getMonth() === selectedMonth
      ) {
        if (!seenStartDates.has(yearMonthStr)) {
          seenStartDates.add(yearMonthStr);
          return true;
        }
      }
      return false;
    });
  }, [
    ATTENDANCE_EVENTS,
    selectedMonth,
    selectedYear,
    selectedEmployee,
    isPending,
  ]);

  const details = useMemo(() => {
    if (detailingFilterEvents?.length === 0) {
      return null;
    }

    const endResult: {
      [key: string]: string[];
    } = {
      Present: [],
      Absent: [],
      "Paid Leave": [],
      "On Leave": [],
      Holiday: [],
      "Not Counting": [],
      "Half-day": [],
    };

    detailingFilterEvents?.forEach((att) => {
      // @ts-nocheck
      // @ts-ignore
      const key = att?.data?.attendance?.status;
      // @ts-nocheck
      // @ts-ignore
      endResult[key]?.push(att?.data?.attendance?.date);
    });

    return endResult;
  }, [detailingFilterEvents, isPending]);

  const mutateCalendarData = () => {
    const month = months.find((m) => m.value === selectedMonth);
    mutate({
      monthName: month?.label as string,
      year: selectedYear,
      employee: selectedEmployee,
    });
  };

  const components: any = {
    event: ({ event }: EventProps<AttendanceEventItem>) => {
      const data = event?.data;
      if (data?.attendance)
        return (
          <AttendanceEvent
            attendance={data?.attendance}
            mutateCalendarData={mutateCalendarData}
          />
          // <AppointmentEvent
          //   appointment={data?.appointment}
          //   isMonthView={view === Views.MONTH}
          // />
        );

      // if (data?.holiday) {
      //   return <HolidayEvent holiday={data?.holiday} />;
      // }

      // if (data?.blockout) {
      //   return <BlockoutEvent blockout={data?.blockout} />;
      // }

      return null;
    },
  };

  const STEP = 5;
  const TIME_SLOTS = 60 / STEP;

  const totalDays = useMemo(() => {
    const date = new Date(selectedYear, selectedMonth + 1, 1);
    date.setDate(date.getDate() - 1);

    return date.getDate();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (SingleTimesheetData) {
      mutate({
        monthName: SingleTimesheetData?.sheet?.month,
        year: SingleTimesheetData?.sheet?.year,
        employee: SingleTimesheetData?.sheet?.employee?._id,
      });
      setSelectedMonth(
        AllMonths.find((t) => t.label === SingleTimesheetData?.sheet?.month)
          ?.value as number
      );
      setSelectedYear(SingleTimesheetData?.sheet?.year);
      setSelectedEmployee(SingleTimesheetData?.sheet?.employee?._id);
    }
  }, [SingleTimesheetData, isDatesheetPending]);

  const { mutate: EditDatesheet, isPending: isClaiming } = useMutation({
    mutationFn: editDatesheetByAdmin(key),
  });

  const onClaiming = () => {
    if (details === null) return;

    if (details["Not Counting"]?.length > 0) {
      return toast("Employee have one or more days with Not Counting Status.");
    }

    EditDatesheet(
      {
        id: sheetId as string,
        info: {
          monthName: months?.find((t) => t.value === selectedMonth)
            ?.label as string,
          absent: details["Absent"]?.length,
          present: details["Present"]?.length,
          paidLeave: details["Paid Leave"]?.length,
          unpaidLeave: details["On Leave"]?.length,
          year: selectedYear,
          employee: selectedEmployee,
          holiday: details["Holiday"]?.length,
          halfDay: details["Half-day"]?.length,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["timesheets-month"],
          });
          onOpenChange(false);
          toast.success("Salary Timesheet Updated!");
        },
        onError: (error) => {
          console.log("ERROR WHILE UPDATING DATESHEET", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-[95%]">
          <AlertDialogHeader>
            <AlertDialogTitle className="tracking-wide">
              Edit Salary Timesheet
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Popover>
                <div className="flex flex-col gap-4">
                  <Label className="-mb-[6px]">Employee</Label>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between",
                        !selectedEmployee && "text-muted-foreground"
                      )}
                    >
                      {selectedEmployee
                        ? employeesData?.find(
                            (emp) => emp?.value === selectedEmployee
                          )?.label
                        : "Select Employee"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search employee..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          {employeesData?.map((emp) => {
                            return (
                              <CommandItem
                                key={emp?.value}
                                value={emp?.label}
                                onSelect={() => {
                                  setSelectedEmployee(emp?.value);
                                }}
                              >
                                {emp?.label}

                                <CheckIcon
                                  className={cn(
                                    "ml-auto h-4 w-4",
                                    selectedEmployee === emp?.value
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </div>
              </Popover>
            </div>
            <div>
              <Label htmlFor="Month">Month</Label>
              <Select
                value={String(selectedMonth)}
                onValueChange={(value) => {
                  setSelectedMonth(Number(value));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a month" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    {months.map((month) => {
                      return (
                        <SelectItem
                          key={month.value}
                          value={String(month.value)}
                          disabled={month.disabled}
                        >
                          {month.label}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-center">
              <div className="flex-1">
                <Label htmlFor="Year">Year</Label>
                <Select
                  value={String(selectedYear)}
                  onValueChange={(value) => {
                    setSelectedYear(Number(value));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a year" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectGroup>
                      {years.map((year) => {
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="self-end"
                onClick={() => {
                  setDate(getMiddleDayOfMonth(selectedYear, selectedMonth));
                  const month = months.find((m) => m.value === selectedMonth);
                  mutate({
                    monthName: month?.label as string,
                    year: selectedYear,
                    employee: selectedEmployee,
                  });
                }}
              >
                See
              </Button>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="h-[70vh] w-[70vw]">
              <Calendar
                events={ATTENDANCE_EVENTS}
                defaultView={"month"}
                // resources={view === Views.DAY ? RESOURCES : undefined}
                components={components}
                toolbar={false}
                date={date}
                view={"month"}
                // onView={setView}
                step={STEP}
                timeslots={TIME_SLOTS}
              />
            </div>

            {details !== null && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span>Total Days - {totalDays}</span>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <span>Present - {details["Present"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Absent - {details["Absent"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Paid Leave - {details["Paid Leave"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Unpaid Leave - {details["On Leave"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Not Counting - {details["Not Counting"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Holiday - {details["Holiday"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Half Days - {details["Half-day"]?.length}</span>
                </div>

                <Separator />

                <div className="flex items-center gap-3">
                  <span>
                    Days Payable -{" "}
                    {details["Present"]?.length +
                      details["Paid Leave"]?.length +
                      details["Holiday"]?.length +
                      details["Half-day"]?.length / 2}
                  </span>
                </div>

                <div className="border rounded-lg p-3 flex gap-2 flex-wrap shadow">
                  <div className="flex flex-col gap-1">
                    <p>({details["Present"]?.length} * 1)</p>
                    <p className="text-xs">Present</p>
                  </div>
                  <div>+</div>
                  <div className="flex flex-col gap-1">
                    <p>({details["Paid Leave"]?.length} * 1)</p>
                    <p className="text-xs">Paid Leave</p>
                  </div>
                  <div>+</div>
                  <div className="flex flex-col gap-1">
                    <p>({details["Holiday"]?.length} * 1)</p>
                    <p className="text-xs">Holiday</p>
                  </div>
                  <div>+</div>
                  <div className="flex flex-col gap-1">
                    <p>({details["Half-day"]?.length} * 0.5)</p>
                    <p className="text-xs">Half Day</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClaiming}>Cancel</AlertDialogCancel>
            <Button onClick={onClaiming} disabled={isClaiming}>
              Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
